import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isInStandaloneMode() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Don't show if already installed
    if (isInStandaloneMode()) return;

    // Check if user dismissed recently (24h cooldown)
    const lastDismissed = localStorage.getItem("ggr_install_dismissed");
    if (lastDismissed && Date.now() - Number(lastDismissed) < 86400000) return;

    // Android/Chrome: capture beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS: show instructions after 5 seconds
    if (isIOS()) {
      const timer = setTimeout(() => setShowIOSPrompt(true), 5000);
      return () => { clearTimeout(timer); window.removeEventListener("beforeinstallprompt", handler); };
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === "accepted") {
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    setDeferredPrompt(null);
    setShowIOSPrompt(false);
    localStorage.setItem("ggr_install_dismissed", String(Date.now()));
  };

  if (dismissed || isInStandaloneMode()) return null;

  // Android install prompt
  if (deferredPrompt) {
    return (
      <div
        className="fixed bottom-20 left-4 right-4 z-[9997] mx-auto max-w-sm rounded-2xl border border-[#dfa625]/30 bg-white shadow-xl p-4"
        data-testid="pwa-install-prompt"
      >
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-xl bg-[#6b6751] flex items-center justify-center">
            <Download size={18} className="text-[#f0ede4]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#514a3f]">Add to Home Screen</p>
            <p className="text-xs text-[#514a3f]/60 mt-0.5">Quick access to Syd's Golf At GGR</p>
          </div>
          <button onClick={handleDismiss} className="text-[#514a3f]/40 hover:text-[#514a3f]" data-testid="pwa-install-dismiss">
            <X size={16} />
          </button>
        </div>
        <button
          onClick={handleInstall}
          className="mt-3 w-full rounded-full bg-[#6b6751] text-[#f0ede4] py-2.5 text-sm font-semibold hover:bg-[#514a3f] transition-colors"
          data-testid="pwa-install-button"
        >
          Install App
        </button>
      </div>
    );
  }

  // iOS instructions
  if (showIOSPrompt) {
    return (
      <div
        className="fixed bottom-20 left-4 right-4 z-[9997] mx-auto max-w-sm rounded-2xl border border-[#dfa625]/30 bg-white shadow-xl p-4"
        data-testid="pwa-ios-prompt"
      >
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-xl bg-[#6b6751] flex items-center justify-center">
            <Share size={18} className="text-[#f0ede4]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#514a3f]">Add to Home Screen</p>
            <p className="text-xs text-[#514a3f]/60 mt-1 leading-relaxed">
              Tap the <strong>Share</strong> button below, then select <strong>"Add to Home Screen"</strong> for quick access.
            </p>
          </div>
          <button onClick={handleDismiss} className="text-[#514a3f]/40 hover:text-[#514a3f]" data-testid="pwa-ios-dismiss">
            <X size={16} />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
